import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const SALES_CHANNEL_OPTIONS = [
  'Contract Buyer',
  'FPO / Cooperative',
  'APMC / Mandi',
  'Local Trader',
];

const VALUE_ADDITION_OPTIONS = ['Yes', 'No'];

const Step4Financial = ({ formData, onChange, errors = {} }) => {
  return (
    <div className="space-y-6">
      {/* Income & Financial Overview */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Income & Financial Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="annual_agricultural_income">
              Annual Agricultural Income (Rs.) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="annual_agricultural_income"
              data-testid="annual-income-input"
              type="number"
              min="0"
              value={formData.annual_agricultural_income || ''}
              onChange={(e) => onChange('annual_agricultural_income', parseFloat(e.target.value) || '')}
              placeholder="Annual income from farming"
              className={errors.annual_agricultural_income ? 'border-red-500' : ''}
            />
            {errors.annual_agricultural_income && <p className="text-sm text-red-500">{errors.annual_agricultural_income}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="total_outstanding_debt">
              Total Outstanding Debt (Rs.) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="total_outstanding_debt"
              data-testid="outstanding-debt-input"
              type="number"
              min="0"
              value={formData.total_outstanding_debt || ''}
              onChange={(e) => onChange('total_outstanding_debt', parseFloat(e.target.value) || '')}
              placeholder="Total current debt (0 if none)"
              className={errors.total_outstanding_debt ? 'border-red-500' : ''}
            />
            {errors.total_outstanding_debt && <p className="text-sm text-red-500">{errors.total_outstanding_debt}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="avg_bank_balance_6mo">
              Avg Bank Balance (6 months) (Rs.) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="avg_bank_balance_6mo"
              data-testid="avg-bank-balance-input"
              type="number"
              min="0"
              value={formData.avg_bank_balance_6mo || ''}
              onChange={(e) => onChange('avg_bank_balance_6mo', parseFloat(e.target.value) || '')}
              placeholder="Average balance over 6 months"
              className={errors.avg_bank_balance_6mo ? 'border-red-500' : ''}
            />
            {errors.avg_bank_balance_6mo && <p className="text-sm text-red-500">{errors.avg_bank_balance_6mo}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bank_account_vintage_years">
              Bank Account Age (years) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="bank_account_vintage_years"
              data-testid="bank-account-vintage-input"
              type="number"
              step="0.1"
              min="0"
              value={formData.bank_account_vintage_years || ''}
              onChange={(e) => onChange('bank_account_vintage_years', parseFloat(e.target.value) || '')}
              placeholder="Years of bank account"
              className={errors.bank_account_vintage_years ? 'border-red-500' : ''}
            />
            {errors.bank_account_vintage_years && <p className="text-sm text-red-500">{errors.bank_account_vintage_years}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="equipment_mechanization_value">
              Estimated Farm Mechanization Asset Value (Rs. in Lakhs)
            </Label>
            <Input
              id="equipment_mechanization_value"
              data-testid="mechanization-asset-input"
              type="number"
              min="0"
              value={formData.equipment_mechanization_value || ''}
              onChange={(e) => onChange('equipment_mechanization_value', parseFloat(e.target.value) || '')}
              placeholder="Value of farm equipment & machinery"
            />
            <p className="text-xs text-gray-500">Tractors, tillers, harvesters, etc.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="banking_transactions_count">
              Banking Transactions (count in last 12 months)
            </Label>
            <Input
              id="banking_transactions_count"
              data-testid="banking-transactions-input"
              type="number"
              min="0"
              value={formData.banking_transactions_count || ''}
              onChange={(e) => onChange('banking_transactions_count', parseInt(e.target.value) || '')}
              placeholder="Total bank transactions"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="kcc_transaction_frequency">
              KCC Transaction Frequency (per year)
            </Label>
            <Input
              id="kcc_transaction_frequency"
              data-testid="kcc-frequency-input"
              type="number"
              min="0"
              value={formData.kcc_transaction_frequency || ''}
              onChange={(e) => onChange('kcc_transaction_frequency', parseInt(e.target.value) || '')}
              placeholder="Kisan Credit Card transactions/year"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="annual_income_retention_ratio_pct">
              Annual Income Retention Ratio (%)
            </Label>
            <Input
              id="annual_income_retention_ratio_pct"
              data-testid="income-retention-input"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.annual_income_retention_ratio_pct || ''}
              onChange={(e) => onChange('annual_income_retention_ratio_pct', parseFloat(e.target.value) || '')}
              placeholder="% of income retained after expenses"
            />
            <p className="text-xs text-gray-500">Percentage of annual income saved/retained</p>
          </div>
        </div>
      </div>

      {/* Other Income */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Additional Income</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="other_income_pct">
              Other Income (% of annual income) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="other_income_pct"
              data-testid="other-income-pct-input"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.other_income_pct ?? ''}
              onChange={(e) => onChange('other_income_pct', parseFloat(e.target.value) || '')}
              placeholder="% of income from non-farm sources"
              className={errors.other_income_pct ? 'border-red-500' : ''}
            />
            <p className="text-xs text-gray-500">Enter 0 if no other income sources</p>
            {errors.other_income_pct && <p className="text-sm text-red-500">{errors.other_income_pct}</p>}
          </div>
        </div>
      </div>

      {/* Government Schemes */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Government Schemes & Support</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* FPO/SHG Membership */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="fpo_shg_membership" className="text-base">
                FPO/SHG Membership
              </Label>
              <p className="text-sm text-gray-500">
                Member of Farmer Producer Organization or Self-Help Group?
              </p>
            </div>
            <Switch
              id="fpo_shg_membership"
              data-testid="fpo-shg-switch"
              checked={formData.fpo_shg_membership || false}
              onCheckedChange={(checked) => onChange('fpo_shg_membership', checked)}
            />
          </div>

          {/* Crop Insurance */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="crop_insurance_status" className="text-base">
                Crop Insurance
              </Label>
              <p className="text-sm text-gray-500">
                Do you have active crop insurance?
              </p>
            </div>
            <Switch
              id="crop_insurance_status"
              data-testid="crop-insurance-switch"
              checked={formData.crop_insurance_status || false}
              onCheckedChange={(checked) => onChange('crop_insurance_status', checked)}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Note: PM-KISAN beneficiary status is now automatically fetched from External Data Integration (Step 8)
        </p>
      </div>

      {/* Market Integration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Market Integration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="sales_channel">Sales Channel</Label>
            <Select
              value={formData.sales_channel || ''}
              onValueChange={(value) => onChange('sales_channel', value)}
            >
              <SelectTrigger id="sales_channel" data-testid="sales-channel-select">
                <SelectValue placeholder="Select primary sales channel" />
              </SelectTrigger>
              <SelectContent>
                {SALES_CHANNEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Primary channel through which produce is sold</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="value_addition">Value Addition</Label>
            <Select
              value={formData.value_addition || ''}
              onValueChange={(value) => onChange('value_addition', value)}
            >
              <SelectTrigger id="value_addition" data-testid="value-addition-select">
                <SelectValue placeholder="Does farmer do value addition?" />
              </SelectTrigger>
              <SelectContent>
                {VALUE_ADDITION_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Processing, grading, or packaging before selling</p>
          </div>
        </div>
      </div>

      {/* Information Tip */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Credit Score Impact</h4>
        <p className="text-sm text-gray-600">
          FPO/SHG membership, crop insurance coverage, and strong market linkages positively influence your credit assessment. Banking activity, KCC usage, and income retention also improve your entitlement score.
        </p>
      </div>
    </div>
  );
};

export default Step4Financial;
